import { store } from '@/store/store'
import type { AppProps } from 'next/app'
import Head from 'next/head'
import React, { useState, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Provider as ReduxProvider } from 'react-redux'
import 'swiper/css'
import 'swiper/css/virtual'
import 'swiper/css/pagination'
import '@/styles/globals.scss'
import '@/styles/Common.scss'
import '@/styles/component/Dialog.scss'
import '@/styles/component/Thumbnail.scss'
import '@/styles/component/Form.scss'
import '@/styles/component/Tag.scss'
import '@/styles/component/Detail.scss'
import '@/styles/component/SwiperMap.scss'
import '@/i18n/config'
import { GoogleAnalytics } from '@/components/GoogleAnalytics'
import { raundUrl } from '@/lib/custom'
import { useCampaignSlug } from '@/lib/useCampaignSlug'
import { useFetchGet } from '@/lib/useFetch'
import PageView from '@/lib/usePageHooks'
import useSWR, { SWRConfig } from 'swr'

function MyApp({ Component, pageProps }: AppProps) {
  const { campaignSlug } = useCampaignSlug()
  const { getWithoutAuth: campaignGet } = useFetchGet<{ campaign: Api.CampaignResBody }>({})
  const { data: campaignData, error: campaignError } = useSWR<{ campaign: Api.CampaignResBody }>(
    raundUrl.campaign,
    campaignGet,
  )

  // react-queryのクライアント定義
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            refetchOnWindowFocus: false,
            retry: false,
          },
        },
      }),
  )

  useEffect(() => {
    const initialize = async () => {
      if (campaignData && campaignData.campaign) {
        const mainColor = campaignData.campaign.mainColor || '#ffc600'
        const subColor = campaignData.campaign.subColor || '#000'
        document.documentElement.style.setProperty('--main-color', mainColor)
        document.documentElement.style.setProperty('--sub-color', subColor)
      } else if (campaignError) {
        const mainColor = '#ffc600'
        const subColor = '#000'
        document.documentElement.style.setProperty('--main-color', mainColor)
        document.documentElement.style.setProperty('--sub-color', subColor)
      } else if (campaignData && campaignData['status'] === 404) {
        const mainColor = '#ffc600'
        const subColor = '#000'
        document.documentElement.style.setProperty('--main-color', mainColor)
        document.documentElement.style.setProperty('--sub-color', subColor)
      }
      const mainColor50 = 'color-mix(in srgb, var(--main-color) 50%, transparent)'
      document.documentElement.style.setProperty('--main-color-50', mainColor50)
      const mainColor20 = 'color-mix(in srgb, var(--main-color) 20%, transparent)'
      document.documentElement.style.setProperty('--main-color-20', mainColor20)
    }
    initialize()
  }, [campaignData, campaignError])
  const fqdnApi = process.env.NEXT_PUBLIC_API_URL.replace('https://', '')
  const fqdnApp = process.env.NEXT_PUBLIC_SITEURL.replace('https://', '')

  return (
    <>
      <Head>
        {/* 本当は_document.tsxに置くのが正解だが、動的に生成したいため_app.tsxに置いている */}
        {!!campaignSlug && campaignSlug !== '[campaign]' && (
          <link rel="manifest" href={`/manifest.json?campaign=${campaignSlug}`} />
        )}
      </Head>
      <QueryClientProvider client={queryClient}>
        <ReduxProvider store={store}>
          <SWRConfig value={{ dedupingInterval: 60_000 }}>
            <GoogleAnalytics gaId={process.env.NEXT_PUBLIC_GAID} linkerDomains={[fqdnApp, fqdnApi]} />
            <PageView />
            <Component {...pageProps} />
          </SWRConfig>
        </ReduxProvider>
      </QueryClientProvider>
    </>
  )
}

export default MyApp
